/**
 * widgets/registry.tsx — registr dostupných widgetů. Jediné místo, kde widget
 * „žije"; stránka i My Panel ho jen skládají. Většina je poskládaná ze šablon
 * (statWidget/listWidget) — deklarace, ne ručně psaná komponenta.
 *
 * Showcase / šablonovací knihovna patří do modulu `_example` (Filip 2026-07-11).
 */
import { Database, HeartPulse, ListChecks, Network } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { StatusDot } from '../components/ui/StatusDot';
import i18n from '../lib/i18n';
import { listWidget } from './templates';
import type { WidgetDef, WidgetSpec } from './types';
import { registerWidgets } from './register';
import { SYSTEM_WIDGETS } from './registry.system';
import { ORCHESTRATOR_WIDGETS } from './registry.orchestrator';
import { NOTIFICATIONS_WIDGETS } from './registry.notifications';
import { TOOLBOX_WIDGETS } from './registry.toolbox';
import { GITLAB_WIDGETS } from './registry.gitlab';
import { DOCKER_WIDGETS } from './registry.docker';
import { CODABLE_WIDGETS } from './registry.codable';
import { SUPABASE_WIDGETS } from './registry.supabase';
import { APPHOST_WIDGETS } from './registry.apphost';
import { NPM_WIDGETS } from './registry.npm';
import { NETWORK_WIDGETS } from './registry.network';
import { RUSTDESK_WIDGETS } from './registry.rustdesk';
import { REGISTRY_MODULE_WIDGETS } from './registry.registry';
import { CERTMGR_WIDGETS } from './registry.certmgr';
import { ACM_SELF_WIDGETS } from './registry.acm-self';
import { TASKS_WIDGETS } from './registry.tasks';
import { MESH_WIDGETS } from './registry.mesh';
import { DASHBOARD_WIDGETS } from './registry.dashboard';
import { PHONE_WIDGETS } from './registry.phone';
import { GEO_WIDGETS } from './registry.geo';
import { DNS_WIDGETS } from './registry.dns';
import { UTM_WIDGETS } from './registry.utm';
import { ADDONSTORE_WIDGETS } from './registry.addonstore';
import { PEOPLE_WIDGETS } from './registry.people';
import { PRINTSERVER_WIDGETS } from './registry.printserver';
import { MeshNodesGlanceWidget } from '../components/mesh/MeshNodesGlance';

// Datum této dávky widgetů — orazítkuje updated (hledání zastaralých).
// docker.* widgety žijí v registry.docker.tsx (2.1 full custom).
export const WIDGET_BUILD = '2026-08-13';
export const WIDGET_SINCE = '2.0.0';

const CORE_WIDGETS: WidgetSpec[] = [
  // Kvalitní mesh seznam (IP, ACM/commit, role, latency) — sdílený s dashboard.mesh-nodes
  {
    id: 'mesh.nodes',
    title: 'Mesh nody',
    module: 'mesh',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Network,
    since: '2.1.0',
    component: MeshNodesGlanceWidget,
  },

  listWidget<{ name: string; status: string }>({
    id: 'toolbox.health-checks',
    title: 'Health checks',
    module: 'toolbox',
    tool: 'toolbox.health_status',
    items: 'tests',
    icon: HeartPulse,
    limit: { mobile: 6, desktop: 20 },
    emptyLabel: i18n.t('dashboard.noHealth', 'Žádné testy'),
    format: 'md',
    row: (t) => (
      <div className='flex items-center gap-2'>
        <StatusDot status={t.status === 'pass' || t.status === 'ok' ? 'running' : 'error'} />
        <span className='text-gray-200'>{t.name}</span>
      </div>
    ),
  }),

  listWidget<{ name: string; module: string; trigger_summary?: string }>({
    id: 'tasks.list',
    title: 'Plánované úlohy',
    module: 'tasks',
    tool: 'tasks.task_list',
    items: 'tasks',
    icon: ListChecks,
    limit: { mobile: 5, desktop: 12 },
    emptyLabel: i18n.t('dashboard.noTasks', 'Žádné úlohy'),
    format: 'md',
    row: (t) => (
      <div className='flex items-center gap-2'>
        <span className='text-gray-200 truncate'>{t.name}</span>
        <Badge variant='gray'>{t.module}</Badge>
        <span className='ml-auto text-xs text-gray-600'>{t.trigger_summary}</span>
      </div>
    ),
  }),

  // dbhost — zašedne na nodu bez modulu dbhost (ukázka inactive stavu).
  listWidget<{ name: string; engine: string; version: string; container_status: string }>({
    id: 'dbhost.instances',
    title: 'Databázové instance',
    module: 'dbhost',
    tool: 'dbhost.list',
    items: 'instances',
    icon: Database,
    limit: { mobile: 4, desktop: 10 },
    emptyLabel: i18n.t('widgets.noInstances', 'Žádné instance'),
    format: 'md',
    row: (d) => (
      <div className='flex items-center gap-2'>
        <StatusDot status={d.container_status === 'running' ? 'running' : 'stopped'} />
        <span className='text-gray-200'>{d.name}</span>
        <Badge variant='blue'>{d.engine} {d.version}</Badge>
      </div>
    ),
  }),
];

export const REGISTRY: WidgetDef[] = registerWidgets(
  [
    ...CORE_WIDGETS,
    ...SYSTEM_WIDGETS,
    ...ORCHESTRATOR_WIDGETS,
    ...TOOLBOX_WIDGETS,
    ...NOTIFICATIONS_WIDGETS,
    ...GITLAB_WIDGETS,
    ...DOCKER_WIDGETS,
    ...CODABLE_WIDGETS,
    ...SUPABASE_WIDGETS,
    ...APPHOST_WIDGETS,
    ...NPM_WIDGETS,
    ...NETWORK_WIDGETS,
    ...RUSTDESK_WIDGETS,
    ...REGISTRY_MODULE_WIDGETS,
    ...CERTMGR_WIDGETS,
    ...ACM_SELF_WIDGETS,
    ...TASKS_WIDGETS,
    ...MESH_WIDGETS,
    ...DASHBOARD_WIDGETS,
    ...PHONE_WIDGETS,
    ...GEO_WIDGETS,
    ...DNS_WIDGETS,
    ...UTM_WIDGETS,
    ...ADDONSTORE_WIDGETS,
    ...PEOPLE_WIDGETS,
    ...PRINTSERVER_WIDGETS,
  ],
  { since: WIDGET_SINCE, build: WIDGET_BUILD },
);
export const REGISTRY_MAP = new Map(REGISTRY.map((w) => [w.id, w]));
